import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import {
	performGetRequest,
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	AdminSettingsRequest,
	AdminSettingsResponse,
	AdminSettingsMap,
	BannerSettings,
} from '../common/types.tsx';
import {
	getAdminSettingsUrl,
	getBannerSettingsUrl,
	transformAdminSettingsResponse,
	transformBannerSettings,
} from '../common/utils.tsx';
import { emptyBannerSettingsResponse } from '../common/constants.tsx';

/**
 * SSR service name header for routing requests through the JSM CMDB Gateway
 */
const SSR_SERVICE_NAME = 'jsm-cmdb-gateway';

/**
 * Merges default options (including SSR headers and credentials) with custom options
 * This ensures cookies are sent for SSR prefetching
 */
const mergeOptionsWithDefaults = (url: string, options: RequestInit | undefined): RequestInit => {
	const defaultOptions = getDefaultOptions(url);

	return {
		...defaultOptions,
		...options,
		headers: {
			...defaultOptions.headers,
			...options?.headers,
		},
	};
};

/**
 * Fetches admin settings by keys for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const getAdminSettingsByKeys = async (
	workspaceId: string,
	settingKeys: AdminSettingsRequest[],
): Promise<AdminSettingsMap> => {
	const url = getAdminSettingsUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		body: JSON.stringify(settingKeys),
		headers: {
			'x-ssr-service-name': SSR_SERVICE_NAME,
			'atl-workspaceid': workspaceId,
		},
	});

	try {
		const response = await performPostRequest<AdminSettingsResponse>(url, mergedOptions);

		return transformAdminSettingsResponse(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 403) {
			return {};
		}

		fireErrorAnalytics({
			meta: {
				id: 'getAdminSettingsByKeys',
				packageName: 'jiraRouterResourcesAssetsAdminNavigationSettings',
				teamName: 'aurum',
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches banner settings for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings
 */
export const getBannerSettings = async (workspaceId: string): Promise<BannerSettings> => {
	const url = getBannerSettingsUrl(workspaceId);

	const mergedOptions = mergeOptionsWithDefaults(url, {
		headers: {
			'x-ssr-service-name': 'jsm-cmdb-gateway',
			'atl-workspaceid': workspaceId,
		},
	});

	try {
		const response = await performGetRequest<BannerSettings>(url, mergedOptions);

		return transformBannerSettings(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 403 || error.statusCode === 404) {
			return emptyBannerSettingsResponse;
		}

		fireErrorAnalytics({
			meta: {
				id: 'getBannerSettings',
				packageName: 'jiraRouterResourcesAssetsAdminBannerSettings',
				teamName: 'aurum',
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates banner settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings/<banner-id>
 */
export const updateBannerSettings = async (
	workspaceId: string,
	bannerId: string,
	bannerUpdates: BannerSettings,
): Promise<BannerSettings> => {
	const url = `${getBannerSettingsUrl(workspaceId)}/${bannerId}`;

	try {
		const response = await performPutRequest<BannerSettings>(
			url,
			mergeOptionsWithDefaults(url, {
				body: JSON.stringify(transformBannerSettings(bannerUpdates)),
			}),
		);
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'updateBannerSettings',
				packageName: 'jiraRouterResourcesAssetsAdminBannerSettings',
				teamName: 'aurum',
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates navigation settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const updateNavigationSettings = async (
	workspaceId: string,
	navigationUpdates: AdminSettingsResponse,
) => {
	const url = getAdminSettingsUrl(workspaceId);

	try {
		const response = await performPutRequest<AdminSettingsMap>(
			url,
			mergeOptionsWithDefaults(url, {
				body: JSON.stringify(navigationUpdates),
			}),
		);
		return response;
	} catch (error: unknown) {
		const err = error instanceof Error ? error : new Error(String(error));
		fireErrorAnalytics({
			meta: {
				id: 'updateNavigationSettings',
				packageName: 'jiraRouterResourcesAssetsAdminNavigationSettings',
				teamName: 'aurum',
			},
			error: err,
		});
		throw err;
	}
};
